import { PHONE_REGEX } from '@/utils/regex.utils';
import { z } from 'zod';
import { userEntityRoleSchema } from '../user-entity-role/user-entity-role.schema';
import { companySchema } from '../company/company.schema';
import i18next from 'i18next';
import { groupSubscriptionSchema } from './group-subscription.schema';
import { groupCustomImageSchema } from './group-custom-image.schema';

export const groupSchema = z.object({
  id: z.string().optional(),
  distributorId: z.string().nullish(),
  name: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:groupNameRequired'),
      });
    }
  }),
  businessIdNumber: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:companyIdNumberRequired'),
      });
    }
    /* else if (!FRENCH_SIREN_REGEX.test(data)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:companyIdNumberFrenchIncorrect'),
      });
    } */
  }),
  addressLine1: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:addressLine1Required'),
      });
    }
  }),
  addressLine2: z.string().nullish(),
  city: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:cityRequired'),
      });
    }
  }),
  state: z.string().nullish(),
  postalCode: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:postalCodeRequired'),
      });
    }
  }),
  country: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:countryRequired'),
      });
    }
  }),
  logo: z.string().nullish(),
  phone: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:phoneNumberRequired'),
      });
    } else if (!PHONE_REGEX.test(data)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:phoneNumberIncorrect'),
      });
    }
  }),
  email: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:emailRequired'),
      });
    } else if (!z.string().email().safeParse(data).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t('common:emailFormatIncorrect'),
      });
    }
  }),
  isWhiteLabel: z.boolean().default(false),
  groupSubscription: groupSubscriptionSchema.nullish(),
  isDistributor: z.boolean().default(false),
  isBranch: z.boolean().default(false),
  companyName: z.string().nullish(),
  branchName: z.string().nullish(),
  automatedEmailingActive: z.boolean().default(false),
  active: z.boolean().default(true),
  companies: z.lazy(() => z.array(companySchema).nullish()).nullish(),
  entityRoles: z.array(userEntityRoleSchema).nullish(),
  legalNotice: z.string().nullish(),
  privacyPolicy: z.string().nullish(),
  partFolderNotFound: z.string().nullish(),
  partContactUs: z.string().nullish(),
  proContactUs: z.string().nullish(),
  moderationMandatory: z.boolean().default(true),
  appWarningMessage: z.string().nullish(),
  cgu: z.string().nullish(),
  dmgApp: z.boolean().default(true),
  // No default: tabs that don't set it omit it, so a stored value is never overwritten
  displayInspectionVideoTutorial: z.boolean().optional(),
  inspectionTutorialVideo: z.string().nullish(),
  groupCustomImage: groupCustomImageSchema.nullish(),
});

export type TGroupSchema = z.infer<typeof groupSchema>;
